import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ToolGraveyardRecord, ToolLibraryRecord, ToolSlotResponse } from '@bridgewerk/shared';
import { getApiClient, type CreateToolLibraryInput, type ToolAssignment } from '../../../lib/api-client.js';
import { toolKeys } from '../../../lib/query-keys.js';

export type { CreateToolLibraryInput, ToolAssignment, ToolGraveyardRecord };

export function useGraveyard(shopId: string | null) {
  return useQuery({
    queryKey: shopId ? toolKeys.graveyard(shopId) : ['tools', 'none', 'graveyard'],
    queryFn: () => getApiClient().getGraveyard(shopId ?? ''),
    enabled: Boolean(shopId),
    staleTime: 30_000,
  });
}

export function useToolAssignments(shopId: string | null, toolId: string) {
  return useQuery({
    queryKey: shopId ? toolKeys.assignments(shopId, toolId) : ['tools', 'none', 'assignments'],
    queryFn: () => getApiClient().getToolAssignments(shopId!, toolId),
    enabled: Boolean(shopId),
    staleTime: 30_000,
  });
}

export interface UseToolLibraryResult {
  readonly data: ToolLibraryRecord[] | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
}

export function useToolLibrary(shopId: string | null): UseToolLibraryResult {
  const query = useQuery({
    queryKey: shopId ? toolKeys.library(shopId) : ['tools', 'none', 'library'],
    queryFn: () => getApiClient().getToolLibrary(shopId ?? ''),
    enabled: Boolean(shopId),
    staleTime: 30_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => { void query.refetch(); },
  };
}

export function useCreateToolLibraryEntry(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateToolLibraryInput) => {
      if (!shopId) throw new Error('shopId is required');
      return getApiClient().createToolLibraryEntry(shopId, input);
    },
    onSuccess: (created) => {
      if (!shopId) return;
      qc.setQueryData<ToolLibraryRecord[]>(toolKeys.library(shopId), (prev) =>
        prev ? [created, ...prev] : [created],
      );
    },
  });
}

export function useUpdateToolLibraryEntry(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ toolId, input }: { toolId: string; input: CreateToolLibraryInput }) => {
      if (!shopId) throw new Error('shopId is required');
      return getApiClient().updateToolLibraryEntry(shopId, toolId, input);
    },
    onSuccess: (updated) => {
      if (!shopId) return;
      qc.setQueryData<ToolLibraryRecord[]>(toolKeys.library(shopId), (prev) =>
        prev?.map((t) => (t.tool_id === updated.tool_id ? updated : t)) ?? [updated],
      );
    },
  });
}

export function useDeleteToolLibraryEntry(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toolId: string) => {
      if (!shopId) throw new Error('shopId is required');
      return getApiClient().deleteToolLibraryEntry(shopId, toolId);
    },
    onSuccess: (_data, toolId) => {
      if (!shopId) return;
      qc.setQueryData<ToolLibraryRecord[]>(toolKeys.library(shopId), (prev) =>
        prev?.filter((t) => t.tool_id !== toolId) ?? [],
      );
    },
  });
}

export interface AssignToolInput {
  readonly machineId:     string;
  readonly toolNumber:    number;
  readonly toolLibraryId: string;
}

export function useAssignToolToSlot(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ machineId, toolNumber, toolLibraryId }: AssignToolInput) => {
      if (!shopId) throw new Error('shopId is required');
      return getApiClient().assignToolToSlot(shopId, machineId, toolNumber, toolLibraryId);
    },
    onSuccess: (_data, { machineId }) => {
      if (!shopId) return;
      void qc.invalidateQueries({ queryKey: toolKeys.list(shopId, machineId) });
    },
  });
}

export function useUnassignTool(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ machineId, toolNumber }: { machineId: string; toolNumber: number }) => {
      if (!shopId) throw new Error('shopId required');
      return getApiClient().unassignToolFromSlot(shopId, machineId, toolNumber);
    },
    onSuccess: (_data, { machineId }) => {
      if (!shopId) return;
      void qc.invalidateQueries({ queryKey: toolKeys.list(shopId, machineId) });
    },
  });
}

export interface SwapSlotsInput {
  readonly machineId: string;
  readonly fromSlot:  ToolSlotResponse;
  readonly toSlot:    ToolSlotResponse | null;
}

export function useSwapToolSlots(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ machineId, fromSlot, toSlot }: SwapSlotsInput) => {
      if (!shopId) throw new Error('shopId required');
      const fromId = fromSlot.tool_definition!.tool_id;
      if (toSlot?.tool_definition) {
        const toId = toSlot.tool_definition.tool_id;
        await getApiClient().assignToolToSlot(shopId, machineId, toSlot.tool_number, fromId);
        await getApiClient().assignToolToSlot(shopId, machineId, fromSlot.tool_number, toId);
      } else if (toSlot) {
        await getApiClient().assignToolToSlot(shopId, machineId, toSlot.tool_number, fromId);
        await getApiClient().unassignToolFromSlot(shopId, machineId, fromSlot.tool_number);
      }
    },
    onSuccess: (_data, { machineId }) => {
      if (!shopId) return;
      void qc.invalidateQueries({ queryKey: toolKeys.list(shopId, machineId) });
    },
  });
}
